import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import StoresService from "../../../api/services/stores.service";
import Loading from "../../common/loading/Loading";
import styles from "./StoresMap.module.scss";

// Fix for default marker icon in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Create custom icon with store name and logo
const createCustomIcon = (storeName: string, logoUrl?: string) => {
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="${storeName}" class="${styles.marker_logo}" />`
    : `<div class="${styles.marker_logo_placeholder}">${storeName.charAt(0).toUpperCase()}</div>`;

  const iconHtml = `
    <div class="${styles.custom_marker}">
      <div class="${styles.marker_label}">${storeName}</div>
      <div class="${styles.marker_content}">
        ${logoHtml}
        <div class="${styles.marker_icon}"></div>
      </div>
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: styles.custom_marker_wrapper,
    iconSize: [120, 80],
    iconAnchor: [60, 80],
    popupAnchor: [0, -80],
  });
};

interface StoreLocation {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  logo_url?: string;
}

const StoresMap = () => {
  const { t } = useTranslation("translation");
  const [stores, setStores] = useState<StoreLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await StoresService.getStoresForMap();
        setStores(response.data?.data || []);
      } catch (err: any) {
        setError(
          err?.response?.data?.error || "Failed to load store locations",
        );
        console.error("Error fetching stores for map:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <div className={styles.no_stores}>
        <p>No stores with location data available</p>
      </div>
    );
  }

  // Calculate center point (average of all store coordinates)
  const centerLat =
    stores.reduce((sum, store) => sum + store.latitude, 0) / stores.length;
  const centerLng =
    stores.reduce((sum, store) => sum + store.longitude, 0) / stores.length;

  return (
    <div className={styles.stores_map_section}>
      <h2 className={styles.section_title}>
        {t("dashboard.store_locations") as string}
      </h2>
      <div className={styles.stores_map_container}>
        <div className={styles.map_wrapper}>
          <MapContainer
            center={[centerLat, centerLng]}
            zoom={10}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {stores.map((store) => (
              <Marker
                key={store.id}
                position={[store.latitude, store.longitude]}
                icon={createCustomIcon(store.name, store.logo_url)}
              >
                <Popup>
                  <div className={styles.popup_content}>
                    {store.logo_url && (
                      <img
                        src={store.logo_url}
                        alt={store.name}
                        className={styles.popup_logo}
                      />
                    )}
                    <strong>{store.name}</strong>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default StoresMap;
